/**
 * AI Service Layer for Lina AI Assistant
 *
 * Handles all API communication with the AI backend:
 *  - Standard chat (POST /api/v1/ai/chat)
 *  - Streaming chat via SSE (POST /api/v1/ai/chat/stream)
 *  - Conversation management (list, get, delete)
 *  - Confirmation/cancellation of destructive actions
 *  - Health check
 *
 * Security considerations:
 *  - All requests use the shared Axios instance (JWT auth)
 *  - SSE streaming uses fetch + ReadableStream (not EventSource, which can't POST)
 *  - Automatic reconnection on transient failures
 *  - AbortController for request cancellation
 *
 * @module services/aiService
 */

import api from './api';

const AI_BASE = '/ai';

// ── Endpoints ────────────────────────────────────────────────────────────────

const ENDPOINTS = {
  CHAT: `${AI_BASE}/chat`,
  CHAT_STREAM: `${AI_BASE}/chat/stream`,
  VOICE_TRANSCRIBE: `${AI_BASE}/voice/transcribe`,
  CONVERSATIONS: `${AI_BASE}/conversations`,
  CONFIRM: (actionId) => `${AI_BASE}/confirm/${actionId}`,
  CANCEL: (actionId) => `${AI_BASE}/cancel/${actionId}`,
  SUGGESTIONS: `${AI_BASE}/suggestions`,
  FEEDBACK: `${AI_BASE}/feedback`,
  PROACTIVE_NOTIFICATIONS: `${AI_BASE}/proactive/notifications`,
  PROACTIVE_PREFERENCES: `${AI_BASE}/proactive/preferences`,
  PROACTIVE_TRIGGERS: `${AI_BASE}/proactive/triggers`,
  HEALTH: `${AI_BASE}/health`,
};

// ── SSE Parser ───────────────────────────────────────────────────────────────

/**
 * Parse SSE lines from a ReadableStream chunk.
 * Handles both single and multi-line data fields.
 *
 * @param {string} text - Raw SSE text chunk
 * @returns {Array<Object>} Parsed event objects
 */
function parseSSEEvents(text) {
  const events = [];
  const lines = text.split('\n');
  let currentData = '';

  for (const line of lines) {
    if (line.startsWith('data: ')) {
      // Start a new data field (append if we're already accumulating)
      currentData += (currentData ? '\n' : '') + line.slice(6);
    } else if (line === '' && currentData) {
      // Empty line marks end of an SSE event — try parsing accumulated data
      try {
        events.push(JSON.parse(currentData));
      } catch {
        // Malformed JSON — discard
      }
      currentData = '';
    }
  }

  // Handle trailing data without a final empty line (partial chunk)
  if (currentData) {
    try {
      events.push(JSON.parse(currentData));
    } catch {
      // Incomplete data — will be completed in the next chunk
    }
  }

  return events;
}

// ── AI Service Class ─────────────────────────────────────────────────────────

class AIService {
  constructor() {
    /** @type {AbortController|null} */
    this._activeStreamController = null;
  }

  // ── Chat ─────────────────────────────────────────────────────────────────

  /**
   * Send a chat message (non-streaming).
   *
   * @param {Object} params
   * @param {string} params.message - User message
   * @param {string} [params.conversationId] - Continue existing conversation
   * @param {Object} [params.context] - Additional context
   * @param {string} [params.context.workspace] - Current workspace type
   * @param {string} [params.context.workspaceId] - Organization ID
   * @param {string} [params.context.currentPage] - Current frontend route
   * @returns {Promise<Object>} { correlationId, response, conversationId, tokensUsed }
   */
  async sendMessage({ message, conversationId, context, attachmentIds, attachments }) {
    const response = await api.post(ENDPOINTS.CHAT, {
      message,
      conversationId,
      context,
      attachmentIds,
      attachments,
    });
    return response.data;
  }

  /**
   * Send a chat message with SSE streaming.
   * Uses fetch + ReadableStream instead of EventSource (EventSource can't POST).
   *
   * @param {Object} params
   * @param {string} params.message
   * @param {string} [params.conversationId]
   * @param {Object} [params.context]
   * @param {Object} callbacks
   * @param {(token: string) => void} callbacks.onToken - Called for each text token
   * @param {(result: Object) => void} callbacks.onDone - Called when stream completes
   * @param {(error: Object) => void} callbacks.onError - Called on error
   * @param {() => void} [callbacks.onStart] - Called when stream begins
   * @returns {Promise<void>}
   */
  async sendMessageStream({ message, conversationId, context, attachmentIds, attachments }, { onToken, onDone, onError, onStart, onAction, onStatus }) {
    // Cancel any active stream
    this.cancelStream();

    const controller = new AbortController();
    this._activeStreamController = controller;

    // Activity timeout — if no SSE events arrive within this window,
    // something is wrong (Gemini hung, network stall, etc.). Abort the
    // stream so the frontend doesn't stay in isStreaming forever.
    const ACTIVITY_TIMEOUT = 45_000; // 45 seconds
    let activityTimer = null;
    let streamCompleted = false; // track whether onDone or onError was received

    const resetActivityTimer = () => {
      if (activityTimer) clearTimeout(activityTimer);
      activityTimer = setTimeout(() => {
        if (streamCompleted) return;
        controller.abort();
        onError?.({
          type: 'error',
          message: 'The response took too long. Please try again.',
        });
      }, ACTIVITY_TIMEOUT);
    };

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL;

      resetActivityTimer(); // Start countdown from fetch initiation

      const response = await fetch(`${apiUrl}${ENDPOINTS.CHAT_STREAM}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ message, conversationId, context, attachmentIds, attachments }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      resetActivityTimer(); // Reset on successful connection

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        resetActivityTimer(); // Reset on every chunk received

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE events
        const events = parseSSEEvents(buffer);
        // Clear buffer after processing (events end with double newline)
        const lastNewline = buffer.lastIndexOf('\n\n');
        if (lastNewline !== -1) {
          buffer = buffer.slice(lastNewline + 2);
        }

        for (const event of events) {
          switch (event.type) {
            case 'start':
              onStart?.(event);
              break;
            case 'status':
              onStatus?.(event);
              break;
            case 'token':
              onToken?.(event.text);
              break;
            case 'action':
              onAction?.(event);
              break;
            case 'done':
              streamCompleted = true;
              onDone?.(event);
              break;
            case 'error':
              streamCompleted = true;
              onError?.(event);
              break;
          }
        }
      }

      // Guard: stream closed (reader.read() returned done: true) but the
      // server never sent a 'done' or 'error' event. Fall back to onError
      // so the frontend cleans up its streaming state.
      if (!streamCompleted) {
        onError?.({
          type: 'error',
          message: 'The connection was interrupted. Please try again.',
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // Stream was intentionally cancelled (or timed out — already handled)
        return;
      }
      onError?.({
        type: 'error',
        message: error.message || 'Streaming failed. Please try again.',
      });
    } finally {
      if (activityTimer) clearTimeout(activityTimer);
      if (this._activeStreamController === controller) {
        this._activeStreamController = null;
      }
    }
  }

  /**
   * Cancel the active streaming request.
   */
  cancelStream() {
    if (this._activeStreamController) {
      this._activeStreamController.abort();
      this._activeStreamController = null;
    }
  }

  // ── Conversations ────────────────────────────────────────────────────────

  /**
   * List user's conversations.
   * @param {Object} [params]
   * @param {number} [params.limit=20]
   * @param {number} [params.skip=0]
   * @returns {Promise<Object>} { conversations, total }
   */
  async listConversations({ limit = 20, skip = 0, context } = {}) {
    const workspaceType = context?.workspace || 'personal';
    const organizationId = workspaceType === 'organization' ? (context?.organizationId || context?.workspaceId) : null;
    const response = await api.get(ENDPOINTS.CONVERSATIONS, {
      params: { limit, skip, workspaceType, ...(organizationId ? { organizationId } : {}) },
    });
    return response.data;
  }

  /**
   * Get a single conversation with full message history.
   * @param {string} conversationId
   * @returns {Promise<Object>} { conversation }
   */
  async getConversation(conversationId, { context } = {}) {
    const workspaceType = context?.workspace || 'personal';
    const organizationId = workspaceType === 'organization' ? (context?.organizationId || context?.workspaceId) : null;
    const response = await api.get(`${ENDPOINTS.CONVERSATIONS}/${conversationId}`, {
      params: { workspaceType, ...(organizationId ? { organizationId } : {}) },
    });
    return response.data;
  }

  /**
   * Delete a conversation.
   * @param {string} conversationId
   * @returns {Promise<Object>} { deleted: true }
   */
  async deleteConversation(conversationId, { context } = {}) {
    const workspaceType = context?.workspace || 'personal';
    const organizationId = workspaceType === 'organization' ? (context?.organizationId || context?.workspaceId) : null;
    const response = await api.delete(`${ENDPOINTS.CONVERSATIONS}/${conversationId}`, {
      params: { workspaceType, ...(organizationId ? { organizationId } : {}) },
    });
    return response.data;
  }

  // ── Actions ──────────────────────────────────────────────────────────────

  /**
   * Confirm a pending destructive action.
   * @param {string} pendingId
   * @param {string} [conversationId] - Conversation ID for context saving
   * @returns {Promise<Object>} { confirmed, result }
   */
  async confirmAction(pendingId, conversationId) {
    const response = await api.post(ENDPOINTS.CONFIRM(pendingId), { conversationId });
    return response.data;
  }

  /**
   * Cancel a pending action.
   * @param {string} pendingId
   * @returns {Promise<Object>} { cancelled, message }
   */
  async cancelAction(pendingId) {
    const response = await api.post(ENDPOINTS.CANCEL(pendingId));
    return response.data;
  }

  // ── Voice ──────────────────────────────────────────────────────────────

  /**
   * Transcribe audio via server-side fallback (Whisper).
   * Used when browser Web Speech API is unavailable.
   * @param {Blob} audioBlob - The audio blob to transcribe
   * @param {string} [locale='en-US'] - BCP 47 locale
   * @returns {Promise<Object>} { text, corrected, confidence, locale, method }
   */
  async transcribeAudio(audioBlob, locale = 'en-US') {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    formData.append('locale', locale);

    const response = await api.post(ENDPOINTS.VOICE_TRANSCRIBE, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  // ── Health ───────────────────────────────────────────────────────────────

  /**
   * Check AI system health.
   * @returns {Promise<Object>}
   */
  async healthCheck() {
    const response = await api.get(ENDPOINTS.HEALTH);
    return response.data;
  }

  // ── Suggestions ──────────────────────────────────────────────────────────

  /**
   * Get contextual suggestion chips from the backend SuggestionEngine.
   * @param {Object} context
   * @param {string} [context.currentPage]
   * @param {string} [context.workspace]
   * @param {string} [context.lastAction]
   * @param {string} [context.projectId]
   * @returns {Promise<Array<{label: string, prompt: string, icon?: string}>>}
   */
  async getSuggestions(context = {}) {
    const response = await api.get(ENDPOINTS.SUGGESTIONS, { params: context });
    return response.data?.data || [];
  }

  // ── Feedback ──────────────────────────────────────────────────────────────

  /**
   * Submit thumbs-up/thumbs-down feedback for an AI message.
   * @param {Object} params
   * @param {string} params.conversationId
   * @param {number} params.messageIndex
   * @param {'positive'|'negative'} params.rating
   * @param {string} [params.comment]
   * @returns {Promise<Object>}
   */
  async submitFeedback({ conversationId, messageIndex, rating, comment }) {
    const response = await api.post(ENDPOINTS.FEEDBACK, {
      conversationId,
      messageIndex,
      rating,
      comment,
    });
    return response.data;
  }

  // ── Proactive Intelligence ────────────────────────────────────────────────

  /**
   * Fetch pending/unread proactive notifications.
   * @param {number} [limit=10]
   * @returns {Promise<Array>}
   */
  async getProactiveNotifications(limit = 10, workspace = null, organizationId = null) {
    const params = { limit };
    if (workspace) params.workspace = workspace;
    if (organizationId) params.organizationId = organizationId;
    const response = await api.get(ENDPOINTS.PROACTIVE_NOTIFICATIONS, {
      params,
    });
    return response.data?.data || [];
  }

  /**
   * Mark a proactive notification as read.
   * @param {string} eventId
   * @returns {Promise<Object>}
   */
  async markProactiveRead(eventId) {
    const response = await api.put(`${ENDPOINTS.PROACTIVE_NOTIFICATIONS}/${eventId}/read`);
    return response.data;
  }

  /**
   * Dismiss a proactive notification.
   * @param {string} eventId
   * @returns {Promise<Object>}
   */
  async dismissProactive(eventId) {
    const response = await api.put(`${ENDPOINTS.PROACTIVE_NOTIFICATIONS}/${eventId}/dismiss`);
    return response.data;
  }

  /**
   * Get proactive notification preferences and available triggers.
   * @returns {Promise<{preferences: Object, availableTriggers: Array}>}
   */
  async getProactivePreferences() {
    const response = await api.get(ENDPOINTS.PROACTIVE_PREFERENCES);
    return response.data?.data || {};
  }

  /**
   * Update proactive notification preferences.
   * @param {Object} prefs - { enabled?, triggers?, quietHours?, maxPerHour? }
   * @returns {Promise<Object>}
   */
  async updateProactivePreferences(prefs) {
    const response = await api.patch(ENDPOINTS.PROACTIVE_PREFERENCES, prefs);
    return response.data?.data || {};
  }

  /**
   * Get list of available proactive triggers.
   * @returns {Promise<Array>}
   */
  async getProactiveTriggers() {
    const response = await api.get(ENDPOINTS.PROACTIVE_TRIGGERS);
    return response.data?.data || [];
  }
}

export default new AIService();
